import dayjs from 'dayjs';
import type { InvoiceRow } from 'lib/api/business/types';
import { getAllInvoices } from 'lib/api/dashboard';
import CessionHistoryDialog from 'lib/common/Dialogs/CessionHistoryDialog';
import { Filter } from 'lib/common/GlobalSearcher';
import PageHeader from 'lib/designSystem/PageHeader';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { formatDate } from 'lib/functions/utils';
import {
  getCessionHistoryColumnConfig,
  useCessionHistoryDialog,
} from 'pages/BusinessProfile/SII/Components/BusinessInvoices/utils/useCessionHistoryDialog';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const LastInvoices = () => {
  const dispatch = useDispatch();
  const [invoices, setInvoices] = useState<
    Array<{ id: string; cessionaries?: Array<Record<string, unknown>> }>
  >([]);
  const [loading, setLoading] = useState(false);
  const startDateDefault = dayjs().subtract(5, 'days');
  const endDateDefault = dayjs();
  const [endDate, setEndDate] = useState(endDateDefault.format('YYYY-MM-DD'));
  const [startDate, setStartDate] = useState(
    startDateDefault.format('YYYY-MM-DD'),
  );
  const [folio, setFolio] = useState('');
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const {
    openCessionHistoryDialog,
    setOpenCessionHistoryDialog,
    selectedCessions,
    handleShowCessionHistory,
  } = useCessionHistoryDialog(invoices as InvoiceRow[]);

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (folio) params.set('folio', folio);
    if (businessIdentifier)
      params.set('businessIdentifier', businessIdentifier);
    if (stakeholderIdentifier)
      params.set('stakeholderIdentifier', stakeholderIdentifier);
    if (stakeholderName) params.set('stakeholderName', stakeholderName);
    if (businessName) params.set('businessName', businessName);
    if (page) {
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
    }
    return params.toString();
  };

  const searchInvoices = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getAllInvoices(dispatch, query);
      setInvoices(response.data);
      setPagination(response.pagination);
    } catch (error) {
      alert('No tienes permisos para ver las facturas');
    }
    setLoading(false);
  };

  const triggerSearch = async () => {
    await searchInvoices({ page: 0, pageSize: PAGE_SIZE });
  };

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'Folio',
      value: folio,
      setter: setFolio,
    },
    {
      type: 'date',
      label: 'Fecha',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
      pairKey: 'endDate',
    },
    {
      type: 'date',
      label: 'Fecha',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
      pairKey: 'startDate',
    },
    {
      label: 'Nombre emisor',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      type: 'text',
      label: 'Rut emisor',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre receptor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      type: 'text',
      label: 'Rut receptor',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
  ];

  useEffect(() => {
    void searchInvoices({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  return (
    <div>
      <PageHeader.WithTitleAndActions
        iconType="invoice"
        title="Facturas"
        subtitle="Acá puedes buscar todas las facturas de tus enrolados"
        globalSearcherProps={{
          filters,
          triggerSearch,
          dispatch,
          disableSearch: loading,
        }}
      />
      <TableWithPagination
        dataHeaders={[
          { key: 'folio', label: 'Folio' },
          { key: 'businessName', label: 'Nombre Emisor' },
          { key: 'businessIdentifier', label: 'Rut Emisor' },
          { key: 'stakeholderName', label: 'Nombre receptor' },
          { key: 'stakeholderIdentifier', label: 'Rut receptor' },
          {
            key: 'issuedDate',
            label: 'Emisión',
            type: 'date',
            formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
          },
          { key: 'receiptDate', label: 'Recepción', type: 'eight-days-date' },
          { key: 'amount', label: 'Monto', type: 'money' },
          { key: 'status', label: 'Estado', type: 'status-chip' },
          {
            key: 'confirmDate',
            label: 'Confirmación',
            type: 'date-colored-ok',
          },
          { key: 'cancelDate', label: 'Reclamo', type: 'date-colored' },
          getCessionHistoryColumnConfig(handleShowCessionHistory),
          {
            key: 'lastCessionDate',
            label: 'Fecha de cesión',
            type: 'date',
            formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
          },
        ]}
        exportToFile="xlsx"
        data={invoices}
        loading={loading}
        setData={setInvoices}
        searchData={searchInvoices}
        setPagination={setPagination}
        pagination={pagination}
      />
      <CessionHistoryDialog
        open={openCessionHistoryDialog}
        onClose={() => setOpenCessionHistoryDialog(false)}
        cessions={selectedCessions}
      />
    </div>
  );
};

export default LastInvoices;
